import { useEffect, useState } from 'react'
import { setAnalyticsUserProperties } from '@/core/analytics'
import { bandFor } from '@/shared/levels'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/core/auth/auth-store'
import { AUTH_BYPASS } from '@/core/auth/dev-bypass'
import { userRepository } from './api/user-repository'
import { overrideProfile } from '@/shared/mock/data'
import { PLAYER_POSITION_LABELS, type UserDoc } from '@/core/models/user'

// `uid!` below is safe by the query's own gate: every one of these hooks sets
// `enabled: uid !== null` (or `!AUTH_BYPASS && uid !== null`), so TanStack
// Query never invokes the fn while uid is null. Asserting is preferable to a
// `?? ''` fallback, which would silently issue a query for the empty-string
// user and return an honest-looking empty result instead of not running.


// The signed-in player's own `users/{uid}` doc. `data === null` after a
// successful fetch means the doc does not exist yet → first run, route to
// /onboarding. Disabled under the dev bypass (no Firebase session) — bypass
// keeps living entirely on the mock store.
export function useMyProfile() {
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  return useQuery({
    queryKey: ['my-profile', uid],
    queryFn: () => userRepository.getMine(uid!),
    enabled: !AUTH_BYPASS && uid !== null,
    staleTime: 5 * 60_000,
  })
}

/** Push a fresh copy of the doc into the query cache after a write. */
export function useSetMyProfileCache() {
  const qc = useQueryClient()
  const uid = useAuthStore((s) => s.user?.uid ?? null)
  return (doc: UserDoc | null) => qc.setQueryData(['my-profile', uid], doc)
}

/**
 * Map the persisted doc to the mock store's identity shape. Stats are ZEROED:
 * for a real account the seeded returning-player numbers (47 partidos, racha
 * 6) would be a lie — real stats arrive with match-result recording. Exported
 * for tests.
 */
export function mockIdentityFromDoc(doc: UserDoc): Record<string, unknown> {
  return {
    ...(doc.profile.name ? { name: doc.profile.name } : {}),
    ...(doc.location.city ? { city: doc.location.city } : {}),
    ...(doc.phone.phoneNumber ? { phone: doc.phone.phoneNumber } : {}),
    ...(doc.padel.skillLevel != null ? { level: doc.padel.skillLevel } : {}),
    ...(doc.padel.position ? { position: PLAYER_POSITION_LABELS[doc.padel.position] } : {}),
    matches: 0,
    streak: 0,
    noShows: 0,
    // Trust facts must be the DOC's, never the returning-player fiction's
    // (same honesty rule as the zeroed stats).
    levelReliability: doc.padel.levelReliability ?? null,
    levelCalibrated: doc.padel.levelSource === 'computed',
  }
}

/**
 * Session hydration bridge — copies the persisted identity facts into the
 * mock store, so every still-stubbed feature (partidos, contactos, share)
 * speaks with the SAME identity the real profile persists (the
 * one-fact-one-store cert discipline, applied across the stub boundary).
 *
 * Returns `true` once the mock store reflects the doc. The caller must NOT
 * render children before then: the mock store is a plain object (not
 * reactive), so a child that reads `getProfile()` during a render that
 * happens before this effect would paint the seeded demo identity and never
 * re-render (the reload-shows-Marce bug caught in the 2026-07-04 smoke).
 */
export function useHydrateMockFromProfile(doc: UserDoc | null | undefined): boolean {
  const [hydratedUid, setHydratedUid] = useState<string | null>(null)
  useEffect(() => {
    if (!doc) return
    overrideProfile(mockIdentityFromDoc(doc))
    // City + level band as BigQuery user_properties, so EVERY event can be
    // sliced by them. Adding `city` to each event instead would leave every
    // event that already shipped permanently unsliceable — GA4 does not
    // backfill. Coarse by design: the band, never the numeric level.
    setAnalyticsUserProperties({
      city: doc.location?.city ?? null,
      levelBand:
        doc.padel?.skillLevel != null ? (bandFor(doc.padel.skillLevel)?.label ?? null) : null,
    })
    setHydratedUid(doc.uid)
  }, [doc])
  return doc != null && hydratedUid === doc.uid
}
